import { useTranslationWithPrefix } from '@hotjar/translations';
import type { Currency } from '../../../../api/common/model/customer';
import type { BillingPeriod } from '../../../../api/common/model/subscription';
import { usePrettifyPrice } from '../../../../utils/cost/prettify-price';
import { PriceReview } from '../../../../features/price-review/PriceReview';
import type { DiscountsType } from '~/subscription-management/components/discount/Discounts';

interface CostChanges {
  priceWithTax: number;
  priceWithoutTax: number;
  priceTax: number;
  currency: Currency;
  accountDiscounts: DiscountsType[];
}

interface Props {
  updated: boolean;
  loading: boolean;
  billingPeriod: BillingPeriod;
  costChanges: CostChanges;
  showTaxes: boolean;
}

export function CostChangesCard({
  updated,
  billingPeriod,
  loading,
  costChanges,
  showTaxes,
}: Props): JSX.Element {
  const { t: tCommon } = useTranslationWithPrefix('checkout:wizard.common');
  const { t: tCostChangesPayLater } = useTranslationWithPrefix(
    'checkout:wizard.cost-changes.pay-later',
  );

  const { prettifyPrice } = usePrettifyPrice(costChanges.currency);
  // TODO: Get the correct date
  const date = undefined;

  const getSubtitle = (): string | undefined => {
    if (date !== undefined) {
      if (updated === true) {
        return tCostChangesPayLater('from-date', { date });
      }

      return tCostChangesPayLater('next-charge-date', { date });
    }

    return undefined;
  };

  return (
    <PriceReview loading={loading}>
      <PriceReview.Taxes
        {...(showTaxes
          ? {
              priceWithoutTax: prettifyPrice(costChanges?.priceWithoutTax),
              priceTax: prettifyPrice(costChanges?.priceTax),
            }
          : undefined)}
        accountDiscounts={costChanges.accountDiscounts}
        currency={costChanges.currency}
        loading={loading}
      />
      <PriceReview.Price
        title={tCostChangesPayLater(
          `title-${billingPeriod.toLocaleLowerCase()}${updated ? '-updated' : ''}`,
        )}
        price={prettifyPrice(showTaxes ? costChanges?.priceWithTax : costChanges?.priceWithoutTax)}
        loading={loading}
      />
      <PriceReview.ExtraInfo
        priceDescription={tCommon(`billing-period-${billingPeriod.toLowerCase()}`)}
        subtitle={getSubtitle()}
      />
    </PriceReview>
  );
}
